import { useMemo } from 'react';
import {
  makeStyles, tokens, Title2, Body1, Caption1, Spinner, Badge,
  MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { useConcurrentTimeline } from '../../../api/hooks';
import type { TimelineProject } from '../../../api/types';

// FEAT-016 Concurrent Projects Timeline. Horizontal bars per project against a shared
// time window. Bar segments: start → target (solid) and target → forecast (lighter,
// only visible when slipping). RAG colour driven by the SlipThresholdGreenDays /
// SlipThresholdAmberDays values from CentralConfig.
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, height: '100%' },
  hint: { color: tokens.colorNeutralForeground2 },
  legend: { display: 'flex', gap: tokens.spacingHorizontalL, alignItems: 'center', flexWrap: 'wrap' },
  legendSwatch: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXS,
  },
  swatch: {
    width: '1rem', height: '1rem', borderRadius: tokens.borderRadiusSmall, display: 'inline-block',
  },
  timeline: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'auto',
  },
  row: { display: 'grid', gridTemplateColumns: '14rem 1fr', alignItems: 'center', gap: tokens.spacingHorizontalM },
  labelCell: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  trackCell: {
    position: 'relative',
    height: '2rem',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusSmall,
  },
  bar: {
    position: 'absolute',
    top: 0, bottom: 0,
    borderRadius: tokens.borderRadiusSmall,
  },
  todayMarker: {
    position: 'absolute', top: 0, bottom: 0, width: '0.125rem',
    backgroundColor: tokens.colorNeutralStroke1Hover,
  },
});

function pct(date: Date | null, windowStart: Date, windowEnd: Date): number | null {
  if (!date) return null;
  const total = windowEnd.getTime() - windowStart.getTime();
  if (total <= 0) return null;
  const offset = date.getTime() - windowStart.getTime();
  return Math.max(0, Math.min(100, (offset / total) * 100));
}

function ragSwatch(rag: TimelineProject['ragState']): string {
  switch (rag) {
    case 'Green': return tokens.colorPaletteGreenBackground3;
    case 'Amber': return tokens.colorPaletteYellowBackground3;
    case 'Red': return tokens.colorPaletteRedBackground3;
    default: return tokens.colorNeutralBackground5;
  }
}

export function ConcurrentTimelinePage() {
  const styles = useStyles();
  const { data, isLoading, error } = useConcurrentTimeline();

  const windowStart = useMemo(() => data ? new Date(data.windowStartUtc) : null, [data]);
  const windowEnd = useMemo(() => data ? new Date(data.windowEndUtc) : null, [data]);
  const todayPct = useMemo(() => {
    if (!windowStart || !windowEnd) return null;
    return pct(new Date(), windowStart, windowEnd);
  }, [windowStart, windowEnd]);

  return (
    <div className={styles.page}>
      <div>
        <Title2>Concurrent Projects Timeline</Title2>
        <Body1 className={styles.hint}>
          Every active project on one timeline. Bar = Start → Target. Light overlay shows Forecast slip past Target. RAG colour from CentralConfig slip thresholds.
        </Body1>
      </div>

      {isLoading ? (
        <Spinner label="Loading projects…" />
      ) : error ? (
        <MessageBar intent="error"><MessageBarBody>{(error as Error).message}</MessageBarBody></MessageBar>
      ) : !data || data.projects.length === 0 ? (
        <Body1>No active projects.</Body1>
      ) : (
        <>
          <div className={styles.legend}>
            <span className={styles.legendSwatch}><span className={styles.swatch} style={{ backgroundColor: ragSwatch('Green') }} /> <Caption1>Green ≤ {data.greenDaysThreshold}d slip</Caption1></span>
            <span className={styles.legendSwatch}><span className={styles.swatch} style={{ backgroundColor: ragSwatch('Amber') }} /> <Caption1>Amber ≤ {data.amberDaysThreshold}d slip</Caption1></span>
            <span className={styles.legendSwatch}><span className={styles.swatch} style={{ backgroundColor: ragSwatch('Red') }} /> <Caption1>Red &gt; {data.amberDaysThreshold}d slip</Caption1></span>
            <span className={styles.legendSwatch}><span className={styles.swatch} style={{ backgroundColor: ragSwatch('Unknown') }} /> <Caption1>Dates not set</Caption1></span>
          </div>

          <div className={styles.timeline}>
            {data.projects.map((p: TimelineProject) => {
              const start = p.startUtc ? new Date(p.startUtc) : null;
              const target = p.targetUtc ? new Date(p.targetUtc) : null;
              const forecast = p.forecastUtc ? new Date(p.forecastUtc) : null;
              const startPct = pct(start, windowStart!, windowEnd!);
              const targetPct = pct(target, windowStart!, windowEnd!);
              const forecastPct = pct(forecast, windowStart!, windowEnd!);
              const colour = ragSwatch(p.ragState);

              return (
                <div key={p.projectId} className={styles.row}>
                  <div className={styles.labelCell}>
                    <Body1>{p.name}</Body1>
                    <Caption1>{p.completedWorkItems}/{p.completedWorkItems + p.openWorkItems} closed · <Badge appearance="filled" color={p.ragState === 'Green' ? 'success' : p.ragState === 'Amber' ? 'warning' : p.ragState === 'Red' ? 'danger' : 'subtle'}>{p.ragState}</Badge></Caption1>
                  </div>
                  <div className={styles.trackCell}>
                    {startPct !== null && targetPct !== null && (
                      <div
                        className={styles.bar}
                        style={{
                          left: `${startPct}%`,
                          width: `${Math.max(0.5, targetPct - startPct)}%`,
                          backgroundColor: colour,
                        }}
                        title={`${start?.toLocaleDateString()} → ${target?.toLocaleDateString()}`}
                      />
                    )}
                    {forecastPct !== null && targetPct !== null && forecastPct > targetPct && (
                      <div
                        className={styles.bar}
                        style={{
                          left: `${targetPct}%`,
                          width: `${forecastPct - targetPct}%`,
                          backgroundColor: colour,
                          opacity: 0.5,
                        }}
                        title={`Forecast slip to ${forecast?.toLocaleDateString()}`}
                      />
                    )}
                    {todayPct !== null && (
                      <div className={styles.todayMarker} style={{ left: `${todayPct}%` }} title="Today" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
